import { ChangeEvent } from "react";

import {
  useCreateProBuyerProject,
  useProBuyerProjectsList,
} from "@/Apollo/hooks/useProBuyerProjects";
import { ProBuyerProjectsAccess } from "@/Apollo/schema";
import { ProProjectNameDialog } from "@/features/pro-buyer-projects/components/ProProjectNameDialog";
import { useCanUseProBuyerProjects } from "@/features/pro-buyer-projects/components/ProProjectsGate";
import { useProProjectsCopy } from "@/features/pro-buyer-projects/hooks/useProProjectsCopy";

/** Same variables as the project chooser, so both read one cached list. */
const PROJECT_CHOICES = { limit: 100, offset: 0 };

const NO_PROJECT = "";
const NEW_PROJECT = "__new__";
const FIELD_ID = "reservation-project";

type ReservationProjectFieldProps = {
  /** The chosen project id, or null for "Geen project". */
  value: string | null;
  onChange: (projectId: string | null) => void;
  /**
   * "+ Nieuw project" was chosen. The caller hides its own modal and opens
   * `ReservationProjectCreateDialog`: the reservation modal sits above every
   * other dialog, so the name dialog cannot open inside it.
   */
  onRequestCreate: () => void;
};

/** True for a Pro Buyer with FULL project access: the only one who may pick or create a project. */
export const useCanPickReservationProject = () =>
  useCanUseProBuyerProjects().access === ProBuyerProjectsAccess.Full;

/**
 * WHO-5959 6e, spec S8. The optional "Voor welk project reserveer je dit?"
 * field in the reservation request. Only for a Pro Buyer with FULL project
 * access (READ_ONLY and NONE see nothing); "+ Nieuw project" creates the
 * project and selects it in one step (spec S4). The project is private: the
 * request the seller sees never carries it.
 */
export const ReservationProjectField = ({
  value,
  onChange,
  onRequestCreate,
}: ReservationProjectFieldProps) => {
  const { tc } = useProProjectsCopy();
  const canWrite = useCanPickReservationProject();
  const { data } = useProBuyerProjectsList({
    variables: PROJECT_CHOICES,
    skip: !canWrite,
  });

  if (!canWrite) {
    return null;
  }

  const projects = data?.proBuyerProjects.items ?? [];

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;

    if (next === NEW_PROJECT) {
      onRequestCreate();

      return;
    }

    onChange(next === NO_PROJECT ? null : next);
  };

  return (
    <div className="flex flex-col space-y-1" data-testid="reservation-project">
      <label htmlFor={FIELD_ID} className="font-body text-sm font-bold">
        {tc("reserveProjectLabel")}
      </label>
      <select
        id={FIELD_ID}
        value={value ?? NO_PROJECT}
        onChange={handleChange}
        className="min-h-11 w-full rounded-md border border-grey-300 bg-white px-3 font-body text-base"
      >
        <option value={NO_PROJECT}>{tc("reserveProjectNone")}</option>
        {projects.map(project => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
        <option value={NEW_PROJECT}>{`+ ${tc("newProject")}`}</option>
      </select>
      <p className="font-body text-sm text-grey-600">{tc("panelPrivacy")}</p>
    </div>
  );
};

type ReservationProjectCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The new project's id, to select it in the reservation request. */
  onCreated: (projectId: string) => void;
};

/** WHO-5959 6e: "+ Nieuw project" from the reservation request (spec S4 inline create). */
export const ReservationProjectCreateDialog = ({
  open,
  onOpenChange,
  onCreated,
}: ReservationProjectCreateDialogProps) => {
  const { tc } = useProProjectsCopy();
  const [createProject] = useCreateProBuyerProject();

  const handleCreate = async (name: string) => {
    const result = await createProject({ variables: { name } });
    const id = result.data?.createProBuyerProject.id;

    if (id) {
      onCreated(id);
    }
  };

  return (
    <ProProjectNameDialog
      open={open}
      onOpenChange={onOpenChange}
      title={tc("newProject")}
      submitLabel={tc("createConfirm")}
      onSubmit={handleCreate}
    />
  );
};
